import { useSortable } from '@dnd-kit/sortable';
import { flexRender, type SortDirection } from '@tanstack/react-table';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import {
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type PropsWithChildren,
  type RefCallback,
  type TouchEvent,
  type MouseEvent as ReactMouseEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  memo,
} from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';

import { TextEllipsis } from '@dynatrace/strato-components/typography';

import {
  horizontalAlignmentCSS,
  verticalAlignmentCSS,
} from './Alignment.sty.js';
import {
  customHeaderDensityCSS,
  dataTableV2ColumnSizingHandleActiveCSS,
  dataTableV2ColumnSizingHandleCSS,
  dataTableV2HeaderCSS,
  dataTableV2HeaderCellActiveInteractionCSS,
  dataTableV2HeaderCellCSS,
  hoveredCSS,
  nonInteractiveHeaderCellCSS,
  leftPositionVar,
} from './DataTableV2Header.sty.js';
import {
  dataTableV2LastLeftPinnedColumnCSS,
  dataTableV2ChildHeaderCSS,
  dataTableV2FirstColumnCSS,
  dataTableV2FirstHeaderRowCSS,
  dataTableV2LastColumnCSS,
  dataTableV2LastHeaderRowCSS,
  dataTableV2ParentHeaderCSS,
  dataTableV2PrefixColumnCSS,
  dataTableV2RowActionsColumnCSS,
  dataTableV2FirstRightPinnedColumnCSS,
  dataTableV2FirstUnpinnedColumnCSS,
} from './DataTableV2Rows.sty.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { Tooltip } from '../../../overlays/tooltip/Tooltip.js';
import { dataTableMessages } from '../../DataTableV2/messages.js';
import {
  DataTableV2ColumnContextProvider,
  DataTableV2IsHeaderContextProvider,
  useDataTableV2,
} from '../contexts/DataTableV2Context.js';
import {
  DATA_TABLE_V2_DEFAULT_HORIZONTAL_ALIGNMENT,
  type DataTableV2AlignmentColumnDef,
} from '../features/ColumnAlignment.js';
import { isDragMotion } from '../features/ColumnOrder/column-order-utils.js';
import { ColumnOrderDragAndDropContext } from '../features/ColumnOrder/ColumnOrderDragAndDropContext.js';
import { calculateLeftPinnedPosition } from '../features/ColumnPinning/column-pinning-utils.js';
import { headerDragAndDropCSS } from '../features/nested-interaction-button.sty.js';
import { isRowDetailsColumnDef } from '../features/RowDetails/RowDetails.js';
import { isRowDragColumnDef } from '../features/RowOrder/RowOrder.js';
import { isRowSelectionColumnDef } from '../features/RowSelection/RowSelection.js';
import { SortingArrowIndicators } from '../features/Sorting/SortingHeaderButton.js';
import { SubRowsHeaderExpander } from '../features/SubRows/SubRowExpanders.js';
import { SubRowsHeaderWidthCorrection } from '../features/SubRows/SubRows.js';
import { InlineCellActionMenu } from '../features/UserActions/InlineCellActionsMenu.js';
import { ROW_ACTIONS_COLUMN } from '../features/UserActions/RowActions.js';
import type { Column } from '../hooks/useTable/types.js';

/**
 * Hook that gets the aria-label for the different table configurations.
 *
 * @param param0 - object containing everything to create the labels according to the current table configuration
 * @returns aria label
 */
function useAriaLabelHeader({
  columnId,
  nextSortingOrder,
  canSort,
  columnDragAndDropMode,
}: {
  columnId: string;
  nextSortingOrder: false | SortDirection | undefined;
  canSort?: boolean;
  columnDragAndDropMode: 'none' | 'enabled' | 'only';
}): string | undefined {
  const intl = useIntl();

  if (canSort && columnDragAndDropMode === 'none') {
    return intl.formatMessage(dataTableMessages.ariaLabelSorting, {
      column: columnId,
      direction: nextSortingOrder,
    });
  } else if (canSort && columnDragAndDropMode === 'enabled') {
    return intl.formatMessage(dataTableMessages.ariaLabelSortingDragAndDrop, {
      column: columnId,
      direction: nextSortingOrder,
    });
  } else if (columnDragAndDropMode !== 'none') {
    return intl.formatMessage(dataTableMessages.ariaLabelDragAndDrop, {
      column: columnId,
    });
  }
}

function InternalMemoizedHeaderCell(
  props: PropsWithChildren<{
    className: string;
    style?: CSSProperties;

    /**
     * RowId from the TanStack/table cell instance.
     */
    rowId: string;
    /**
     * RowIndex based on the virtualization index.
     */
    rowIndex: number;
    /**
     * ColumnId from the TanStack/table cell instance.
     */
    columnId: string;

    /**
     * ColumnIndex based on the virtualization index.
     */
    columnIndex: number;

    /**
     * Measure function that is called when the cell is
     * rendered to the dom.
     */
    measure: RefCallback<HTMLElement> | undefined;

    /**
     * Defines the role of the header cell.
     */
    role: 'columnheader' | 'presentation';

    /**
     * Determines whether the DataTableV2 has expanding rows (subRows).
     */
    enableExpanding?: boolean;

    /**
     * Determines if the given cell within the column contains the sub row expanding trigger.
     */
    isSubRowIndicatorColumn?: boolean;

    /**
     * Whether all rows are currently expanded.
     */
    isAllRowsExpanded: boolean;

    /**
     * Defines if the header cell is a placeholder cell.
     */
    isPlaceholder?: boolean;

    /**
     * Defines if the header is the last in a group
     */
    isLastColumnInGroup?: boolean;

    /**
     * Defines if the current header can receive user actions and displays a menu trigger.
     */
    canUserAction?: boolean;

    /**
     * Defines the drag and drop interaction mode for the current header.
     */
    columnDragAndDropMode: 'none' | 'only' | 'enabled';

    /**
     * Defines if the column is a built-in column.
     */
    isBuiltin?: boolean;

    /**
     * Defines if the column can be sorted.
     */
    canSort?: boolean;

    /** Defines if the current column is sorted and if yes, in which direction */
    isSorted?: false | SortDirection;

    /** Defines if the current column can be resized */
    canResize?: boolean;
    /**
     * Defines if the current column is right now resizing
     */
    isResizing?: boolean;

    /**
     * Gives access to the resize handler.
     */
    getResizeHandler?: () => (
      event: ReactMouseEvent<HTMLButtonElement> | TouchEvent<HTMLButtonElement>,
    ) => void;
    /**
     * Gives access to the keyboard resize handler.
     */
    getKeyboardResizeHandler: () => (
      event: ReactKeyboardEvent<HTMLButtonElement>,
    ) => void;
    /**
     * Gives access to the resize expand handler.
     */
    getResizeExpandHandler?: () => (
      event: ReactMouseEvent<HTMLButtonElement>,
    ) => void;

    /**
     * Defines if the column can be sorted by a click on the header.
     */
    canSortByHeaderClick?: boolean;

    /** Defines the aria sort attribute */
    ariaSortAttribute?: 'none' | 'ascending' | 'descending' | 'other';

    /**
     * Defines if the column is set to sort descending first.
     */
    sortDescFirst?: boolean;

    /** Defines the next sorting order, if sorting would be toggled. */
    nextSortingOrder?: SortDirection | false;

    /**
     * Defines the grid position the header cell will be mounted into
     */
    gridPosition: {
      gridColumnStart: number;
      gridColumnEnd: number | `span ${number}`;
      gridRowStart: number;
      gridRowEnd: number | `span ${number}`;
    };

    /**
     * Gives access to the sorting handler.
     */
    getToggleSortingHandler?: () => undefined | ((event: unknown) => void);

    /** Row density of the current rendered row. */
    rowDensity: 'default' | 'condensed' | 'comfortable';
    /** Column alignment based on the column definition. */
    alignment: DataTableV2AlignmentColumnDef['alignment'];
    /**
     * The table's virtualization container.
     */
    virtualizationContainer?: HTMLDivElement;
    /**
     * The header column.
     */
    column?: Column<unknown, unknown>;
    /**
     * Defines css positioning for the left pinned columns.
     */
    leftPosition?: string;
  }>,
) {
  const {
    rowId,
    rowIndex,
    columnId,
    columnIndex,
    role,
    isPlaceholder,
    measure,
    getToggleSortingHandler,
    canSort,
    isSorted,
    ariaSortAttribute,
    nextSortingOrder,
    isLastColumnInGroup,
    enableExpanding,
    isSubRowIndicatorColumn,
    isAllRowsExpanded,
    sortDescFirst,
    canSortByHeaderClick,
    children,
    className,
    canUserAction,
    columnDragAndDropMode,
    canResize,
    isResizing,
    gridPosition,
    getResizeHandler,
    getKeyboardResizeHandler,
    getResizeExpandHandler,
    rowDensity,
    column,
    leftPosition,
  } = props;

  const intl = useIntl();
  const table = useDataTableV2();

  const [textOverflow, setTextOverflow] = useState(false);
  const portalRef = useRef<HTMLElement>(null);
  const { setNodeRef, isDragging, attributes, listeners } = useSortable({
    id: columnId,
    data: {
      portalRef,
      column,
      columnIndex,
    },
  });
  const touchStart = useRef<TouchEvent>();

  const divRef = useRef<HTMLDivElement>(null);
  const mergedRef = useMergeRefs<HTMLElement | null>([
    setNodeRef,
    measure,
    divRef,
  ]);

  const style = useMemo(() => {
    return {
      ...gridPosition,
    };
  }, [gridPosition]);

  const ariaLabelHeader = useAriaLabelHeader({
    columnId,
    nextSortingOrder,
    canSort,
    columnDragAndDropMode,
  });

  // If the header is a placeholder, do not render any content.
  if (isPlaceholder) {
    return null;
  }

  const isInteractiveHeader = canUserAction || canSortByHeaderClick;
  let headerContent = children;

  const draggedHeaderColumn = table.getState().draggedHeaderColumn;
  const dragAndDropProps =
    columnDragAndDropMode !== 'none'
      ? {
          ...attributes,
          draggable: true,
          onDragStart: (event: DragEvent<HTMLElement>) =>
            listeners?.onMouseDown(event),
          onTouchStart: (event: TouchEvent<HTMLElement>) =>
            (touchStart.current = event),
          onTouchMove: (event: TouchEvent<HTMLElement>) =>
            isDragMotion(event, touchStart?.current) &&
            listeners?.onTouchStart(event),
        }
      : undefined;

  // for text headers a tooltip should be rendered when the text overflows
  // if the header is interactive, the tooltip will be put on the button
  if (typeof children === 'string' && !isInteractiveHeader) {
    headerContent = (
      <Tooltip disabled={!textOverflow} text={children} placement="left-start">
        <TextEllipsis
          onTextOverflow={setTextOverflow}
          className={nonInteractiveHeaderCellCSS}
          {...(textOverflow && {
            style: {
              pointerEvents: 'auto',
              width: 'fit-content',
              maxWidth: '100%',
            },
          })}
        >
          {children}
        </TextEllipsis>
      </Tooltip>
    );
  } else if (typeof children === 'string' && isInteractiveHeader) {
    headerContent = (
      <TextEllipsis
        onTextOverflow={setTextOverflow}
        style={{ width: 'fit-content', maxWidth: '100%' }}
      >
        {children}
      </TextEllipsis>
    );
  }

  /**
   * When the cell is hovered, the inline cell actions trigger appears.
   * To prevent the column width from increasing, we explicitly set the column
   * width to its current pixel value before setting the hovered style
   * (which causes the cell action trigger to be shown).
   */
  const onCellMouseEnter = () => {
    if (divRef.current) {
      // set the current width explicitly in pixels
      divRef.current.style.width = `${
        divRef.current.getBoundingClientRect().width
      }px`;
      // set the hovered state
      divRef.current.classList.add(hoveredCSS);
    }
  };

  /**
   * When the cell is not hovered anymore (or the cell action menu is closed),
   * we first remove the hovered style (which causes the cell action trigger to disappear).
   * Then, we reset the column width to the state before the cell action trigger was shown.
   */
  const resetWidth = () => {
    if (divRef.current) {
      // remove the hovered state
      divRef.current.classList.remove(hoveredCSS);
      // reset column width
      divRef.current.style.removeProperty('width');
    }
  };

  const onCellMouseLeave = () => {
    if (divRef.current) {
      const cellAction = divRef.current.querySelector(
        '[data-trigger="useraction"]',
      );
      const isOpen = cellAction?.getAttribute('data-state') === 'open';
      if (isOpen) {
        // keep the fixed width while the cell actions menu is open
        return;
      }
      resetWidth();
    }
  };

  const onCellActionClose = () => {
    resetWidth();
  };

  const fullHeader = (
    <>
      <div
        {...dragAndDropProps}
        key={`Header-${columnId}`}
        ref={mergedRef}
        role={role}
        data-headerid={rowId}
        data-header-row={rowIndex}
        data-column={columnIndex}
        data-columnid={columnId}
        className={clsx(className, {
          // add grab cursor styling only if new interaction pattern and column drag and drop are enabled but sorting is not
          [`${headerDragAndDropCSS.enabled}`]:
            !isDragging &&
            columnDragAndDropMode !== 'none' &&
            !canSortByHeaderClick,
          [headerDragAndDropCSS.inactive]: draggedHeaderColumn !== undefined,
        })}
        aria-sort={ariaSortAttribute}
        data-sort-desc-first={sortDescFirst ?? false}
        aria-label={ariaLabelHeader}
        style={{
          ...assignInlineVars({
            [leftPositionVar]: leftPosition,
          }),
          ...style,
        }}
        onMouseEnter={onCellMouseEnter}
        onMouseLeave={onCellMouseLeave}
        onClick={(event) => {
          if (
            !(event.target instanceof HTMLElement) &&
            !(event.target instanceof SVGElement)
          ) {
            return;
          }

          const { target } = event;
          const headerCell =
            'headerRow' in target.dataset
              ? target
              : target.closest('[role="columnheader"]');
          if (headerCell === target) {
            getToggleSortingHandler?.()?.(event);
            return;
          }

          // TODO: Discuss how we want to handle this here? we cannot only check for the direct cell target, as any inner element would violate this trigger.
          let hitInteractiveElement = false;
          if (!headerCell || headerCell === target) {
            return;
          }

          let currentElement: HTMLElement | SVGElement | null = target;
          do {
            hitInteractiveElement =
              currentElement?.getAttribute('tabindex') !== null ||
              currentElement?.tagName === 'A' ||
              currentElement?.tagName === 'BUTTON' ||
              currentElement?.tagName === 'LABEL' ||
              currentElement?.tagName === 'INPUT' ||
              currentElement?.tagName === 'SELECT';

            if (hitInteractiveElement) {
              break;
            }
            currentElement = currentElement?.parentElement;
          } while (currentElement !== headerCell && currentElement !== null);

          if (!hitInteractiveElement) {
            getToggleSortingHandler?.()?.(event);
          }
        }}
      >
        {/* Only render the subrow expander in the first visible column */}
        {enableExpanding === true && isSubRowIndicatorColumn ? (
          <SubRowsHeaderExpander isAllRowsExpanded={isAllRowsExpanded} />
        ) : null}

        {headerContent}

        {canSort && (
          <SortingArrowIndicators isSorted={isSorted} rowDensity={rowDensity} />
        )}

        {canUserAction && (
          <InlineCellActionMenu
            rowId={rowId}
            columnId={columnId}
            type="header"
            aria-label={intl.formatMessage({
              defaultMessage: 'Trigger cell action',
              description: 'Aria label for DataTable cell actions trigger',
              id: 'SOikNJR+NVkSUago',
            })}
            data-header-row={rowIndex}
            data-column={columnIndex}
            onClose={onCellActionClose}
          />
        )}

        {/* Only render the subrow expander in the first visible column */}
        {enableExpanding === true && isSubRowIndicatorColumn ? (
          <SubRowsHeaderWidthCorrection />
        ) : null}
      </div>

      {!isDragging && canResize && (
        <button
          type="button"
          key={`Header-${columnId}-resize-handler`}
          aria-label={intl.formatMessage(
            {
              defaultMessage: 'Change size for "{column}" column',
              description: 'Aria label for DataTable header resize handler',
              id: 'grMCOLy6cDAqdO7V',
            },
            {
              column: columnId,
            },
          )}
          data-header-row={rowIndex}
          data-column={columnIndex}
          data-trigger="resize"
          style={{
            gridColumnStart: gridPosition.gridColumnStart,
            gridColumnEnd: gridPosition.gridColumnEnd,
            gridRowStart: isLastColumnInGroup ? 1 : gridPosition.gridRowStart,
            gridRowEnd: isLastColumnInGroup
              ? `span ${table.getHeaderGroups().length}`
              : gridPosition.gridRowEnd,
          }}
          className={clsx(dataTableV2ColumnSizingHandleCSS, {
            [dataTableV2ColumnSizingHandleActiveCSS]: isResizing,
          })}
          onMouseDown={getResizeHandler?.()}
          onTouchStart={getResizeHandler?.()}
          onKeyUp={getKeyboardResizeHandler?.()}
          onDoubleClick={getResizeExpandHandler?.()}
        />
      )}
    </>
  );

  if (isDragging) {
    return createPortal(fullHeader, portalRef.current || document.body);
  }

  return fullHeader;
}

const DataTableV2HeaderCell = memo(InternalMemoizedHeaderCell);

export function DataTableV2Header() {
  const table = useDataTableV2();
  const headerGroups = table?.getHeaderGroups();

  const leftPinnedColumns = table.getLeftVisibleLeafColumns();
  const rightPinnedColumns = table.getRightVisibleLeafColumns();
  const visualVariants = table.getVisualVariantOptions();

  const pinnedColumnsOrder = table.getState().columnPinning.left;
  const isRowThresholdEnabled =
    table.options.hasThresholds &&
    table.options.rowThresholds?.some((threshold) => threshold.type === 'pill');
  const prefixColumnsCount = table.getBuiltinColumnsCount();

  const headerCells = headerGroups.map((headerGroup, rowIndex, rows) => {
    return headerGroup.headers.map((header, columnIndex, columns) => {
      return (
        <DataTableV2HeaderCell
          key={header.id}
          role={
            header.column.columnDef.meta?.spacer === true
              ? 'presentation'
              : 'columnheader'
          }
          isPlaceholder={header.isPlaceholder}
          isLastColumnInGroup={
            header.column.parent?.getVisibleLeafColumns().at(-1) ===
            header.column
          }
          measure={header.getMeasureRef()}
          rowIndex={rowIndex}
          rowId={headerGroup.id}
          columnIndex={
            rowIndex === 0
              ? header.getGridPosition(columnIndex).gridColumnStart - 1
              : columnIndex
          }
          columnId={header.column.id}
          canSortByHeaderClick={header.getCanSortByHeaderClick()}
          canUserAction={header.column.getCanUserActions()}
          columnDragAndDropMode={header.getColumnDragAndDropMode()}
          isBuiltin={header.column.columnDef.meta?.builtin}
          canSort={header.column.getCanSort()}
          isSorted={header.column.getIsSorted()}
          sortDescFirst={header.column.columnDef.sortDescFirst}
          gridPosition={header.getGridPosition(columnIndex)}
          getToggleSortingHandler={header.column.getToggleSortingHandler}
          ariaSortAttribute={header.getAriaSortAttribute()}
          nextSortingOrder={header.column.getNextSortingOrder()}
          enableExpanding={table.options.enableExpanding}
          isSubRowIndicatorColumn={
            table.options.enableExpanding === true &&
            table.getSubRowIndicatorColumnId() === header.column.id
          }
          isAllRowsExpanded={table.getIsAllRowsExpanded()}
          canResize={header.column.getCanResize()}
          isResizing={header.column.getIsResizing()}
          getResizeHandler={header.getResizeHandler}
          getKeyboardResizeHandler={header.getKeyboardResizeHandler}
          getResizeExpandHandler={header.getResizeExpandHandler}
          rowDensity={table.getVisualVariantOptions().rowDensity}
          alignment={header.column.columnDef.alignment}
          virtualizationContainer={table.getVirtualizationContainer()}
          column={header.column}
          leftPosition={calculateLeftPinnedPosition(
            header.column.getIndex('left'),
            pinnedColumnsOrder,
            visualVariants.rowDensity,
            isRowThresholdEnabled,
          )}
          className={clsx(
            dataTableV2HeaderCellCSS({
              rowDensity: header.column.columnDef.meta?.hasCustomHeaderRenderer
                ? 'customHeaderRenderer'
                : visualVariants.rowDensity,
              pinnedColumn: header.column.getIsPinned(),
              verticalDividers:
                visualVariants.verticalDividers ||
                table.getHeaderGroups().length > 1,
              sortable: header.column.getCanSort(),
              isDragAndDropDisabled: header.getIsDragAndDropDisabled(),
            }),
            horizontalAlignmentCSS[
              header.column.columnDef.alignment ??
                DATA_TABLE_V2_DEFAULT_HORIZONTAL_ALIGNMENT
            ],
            verticalAlignmentCSS[header.getVerticalAlignment()],
            {
              [dataTableV2FirstColumnCSS]: columnIndex === 0,
              [dataTableV2FirstUnpinnedColumnCSS]:
                prefixColumnsCount > 0 && columnIndex === prefixColumnsCount,
              [dataTableV2LastColumnCSS]: columnIndex === columns.length - 1,
              [dataTableV2FirstHeaderRowCSS]: rowIndex === 0,
              [dataTableV2LastLeftPinnedColumnCSS]:
                leftPinnedColumns.at(-1)?.id === header.column?.id,
              [dataTableV2FirstRightPinnedColumnCSS]:
                rightPinnedColumns.at(0)?.id === header.column?.id,
              [dataTableV2LastHeaderRowCSS]: rowIndex === rows.length - 1,
              [dataTableV2ParentHeaderCSS]: header.column.columns.length > 0,
              [dataTableV2ChildHeaderCSS]: !!header.column.parent,
              [dataTableV2RowActionsColumnCSS]:
                header.column.id === ROW_ACTIONS_COLUMN.id,
              [dataTableV2PrefixColumnCSS]:
                isRowSelectionColumnDef(header.column.columnDef) ||
                isRowDetailsColumnDef(header.column.columnDef) ||
                isRowDragColumnDef(header.column.columnDef),
              // TODO: Meh, this is not really nice
              [`${dataTableV2HeaderCellActiveInteractionCSS}`]:
                table.getState().floatingMenuInfo?.columnId ===
                  header.column.id &&
                table.getState().floatingMenuInfo?.rowId === null,
              [customHeaderDensityCSS[visualVariants.rowDensity]]:
                header.column.columnDef.meta?.hasCustomHeaderRenderer,
            },
          )}
        >
          {header.column.columnDef.meta?.hasCustomHeaderRenderer ? (
            <DataTableV2ColumnContextProvider value={header.column}>
              {flexRender(header.column.columnDef.header, header.getContext())}
            </DataTableV2ColumnContextProvider>
          ) : (
            flexRender(header.column.columnDef.header, header.getContext())
          )}
        </DataTableV2HeaderCell>
      );
    });
  });

  const headerContent = table.options.enableColumnOrdering ? (
    <ColumnOrderDragAndDropContext>{headerCells}</ColumnOrderDragAndDropContext>
  ) : (
    // If column ordering is not enabled, don't use the ColumnOrderDragAndDropContext
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>{headerCells}</>
  );

  return (
    <DataTableV2IsHeaderContextProvider>
      <div
        role="rowgroup"
        data-rowgroup="header"
        className={dataTableV2HeaderCSS}
      >
        {headerContent}
      </div>
    </DataTableV2IsHeaderContextProvider>
  );
}

DataTableV2Header.displayName = 'DataTableV2Header';
